import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";

export function ReportAction({ action, rows, onDone }) {
  const [options, setOptions] = useState([]);
  const [fallback, setFallback] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const keys = [...new Set(rows.map((r) => r[action.key]).filter(Boolean))];

  useEffect(() => {
    if (action.fallback?.source) api.get(action.fallback.source).then((r) => setOptions(r.data)).catch(() => {});
  }, [action.fallback?.source]);

  const fire = async () => {
    if (!keys.length) { toast.info("No rows to act on"); return; }
    if (action.confirm && !window.confirm(action.confirm.replace("{n}", keys.length))) return;
    setBusy(true);
    try {
      const body = { keys, ...(action.fallback && fallback ? { [action.fallback.param]: fallback } : {}) };
      const { data } = await api.post(action.endpoint, body);
      setResult(data);
      if (data.created?.length) toast.success(data.message || "Done"); else toast.warning(data.message || "Nothing created");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-5 py-3 border-b border-zinc-200 bg-amber-50/40 print:hidden" data-testid="report-action">
      <div className="flex flex-wrap items-end gap-3">
        <button onClick={fire} disabled={busy || keys.length === 0} className="bg-[#FF3E00] text-white text-[10px] font-semibold px-4 py-2 hover:bg-zinc-950 flex items-center gap-1 disabled:opacity-40" data-testid="report-action-btn">
          <Lightning size={12} weight="fill" /> {action.label} ({keys.length})
        </button>
        {action.fallback && (
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">{action.fallback.label}</span>
            <select value={fallback} onChange={(e) => setFallback(e.target.value)} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-xs outline-none min-w-[220px]" data-testid="report-action-fallback">
              <option value="">— none —</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o[action.fallback.option_label] || o.id}</option>)}
            </select>
          </label>
        )}
        <span className="text-[10px] font-mono text-zinc-500">Acts on the rows currently shown — use the filter box to narrow the selection.</span>
      </div>
      {result && (
        <div className="mt-3 text-xs space-y-1" data-testid="report-action-result">
          {result.created?.map((c) => (
            <div key={c.po_no} className="flex flex-wrap items-center gap-2" data-testid={`report-action-created-${c.po_no}`}>
              <span className="text-emerald-700 font-mono">✓</span>
              <Link to="/scm?tab=Purchase%20Orders" className="font-mono underline">{c.po_no}</Link>
              <span>→ {c.supplier} · {c.lines} line{c.lines === 1 ? "" : "s"} · {c.skus.join(", ")} · total {Number(c.total).toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
              {c.approval_status === "pending" && <span className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700">APPROVAL PENDING</span>}
            </div>
          ))}
          {result.skipped?.map((sk) => (
            <div key={sk.sku} className="text-zinc-500" data-testid={`report-action-skipped-${sk.sku}`}><span className="font-mono text-[#D0021B]">✗</span> {sk.sku}: {sk.reason}</div>
          ))}
        </div>
      )}
    </div>
  );
}
